/**
 * OnboardingTour — Welcome carousel + step-by-step tooltip tour
 *
 * Phase 1: Welcome modal with swipeable feature cards
 * Phase 2: Tooltip highlights pointing at sidebar nav items
 *
 * Shows once on first login (tracked via localStorage).
 *
 * API_TODO: Track onboarding completion server-side:
 *   PUT /api/users/me { onboardingComplete: true }
 */
import { useState, useEffect, useRef } from 'react';
import AscendingK from './AscendingK';

// ── Feature cards for the welcome carousel ──
const WELCOME_SLIDES = [
  {
    emoji: '👋',
    title: 'Welcome to Connekted in',
    subtitle: 'Your digital business card, reimagined.',
    description: 'Create a professional profile, share it with a QR code, and never lose a connection again.',
  },
  {
    emoji: '✏️',
    title: 'Build your profile',
    subtitle: 'Profile Editor',
    description: 'Add your name, title, company, links, and contact details. Toggle what\'s visible. It takes 2 minutes.',
  },
  {
    emoji: '📱',
    title: 'Your QR card',
    subtitle: 'Branded halftone QR',
    description: 'A unique QR code with your photo embedded. Add it to Apple Wallet or Google Wallet for instant sharing.',
  },
  {
    emoji: '🔔',
    title: 'Scan notifications',
    subtitle: 'Your personal CRM',
    description: 'See who scanned your card, when, and where. Add notes, organise contacts into lists, and filter by role.',
  },
  {
    emoji: '🚀',
    title: 'Grow your network',
    subtitle: 'Referrals & sharing',
    description: 'Invite others to Connekted in and earn points. Share your card across social media with one tap.',
  },
];

// ── Tooltip tour steps (targets sidebar nav items by index) ──
const TOUR_STEPS = [
  {
    navIndex: 0,
    title: 'Profile Editor',
    description: 'Start here — fill in your details, add links, and upload a photo. This is your digital business card.',
  },
  {
    navIndex: 1,
    title: 'Scan Page Preview',
    description: 'See exactly what people see when they scan your QR code. Preview your card before sharing.',
  },
  {
    navIndex: 2,
    title: 'Your QR Card',
    description: 'Download your branded QR code, or add it to your phone\'s wallet for instant sharing at events.',
  },
  {
    navIndex: 3,
    title: 'Scan Notifications',
    description: 'Your personal CRM — every scan is logged. Search, filter by role, add notes, and organise contacts.',
  },
  {
    navIndex: 4,
    title: 'Referrals',
    description: 'Share Connekted in with friends and colleagues. Track your invites and earn rewards.',
  },
];

export default function OnboardingTour({ onComplete }) {
  const [phase, setPhase] = useState('carousel'); // 'carousel' | 'tour' | null
  const [slideIndex, setSlideIndex] = useState(0);
  const [tourStep, setTourStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  // Position tooltip next to the sidebar nav item
  useEffect(() => {
    if (phase !== 'tour') return;

    const step = TOUR_STEPS[tourStep];
    const navItems = document.querySelectorAll('.nav-item');
    const target = navItems[step.navIndex];

    if (target) {
      const rect = target.getBoundingClientRect();
      // Add highlight class
      navItems.forEach((n) => n.classList.remove('tour-highlight'));
      target.classList.add('tour-highlight');

      setTooltipPos({
        top: rect.top + rect.height / 2,
        left: rect.right + 16,
      });
    }

    return () => {
      navItems.forEach((n) => n.classList.remove('tour-highlight'));
    };
  }, [phase, tourStep]);

  const nextSlide = () => {
    if (slideIndex < WELCOME_SLIDES.length - 1) {
      setSlideIndex(slideIndex + 1);
    } else {
      // Move to tooltip tour
      setPhase('tour');
    }
  };

  const prevSlide = () => {
    if (slideIndex > 0) setSlideIndex(slideIndex - 1);
  };

  const skipCarousel = () => {
    setPhase('tour');
  };

  const nextTourStep = () => {
    if (tourStep < TOUR_STEPS.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      finishTour();
    }
  };

  const prevTourStep = () => {
    if (tourStep > 0) setTourStep(tourStep - 1);
  };

  const finishTour = () => {
    // Clean up highlights
    document.querySelectorAll('.nav-item').forEach((n) => n.classList.remove('tour-highlight'));
    setPhase(null);
    onComplete();
  };

  // ── Phase 1: Welcome carousel ──
  if (phase === 'carousel') {
    const slide = WELCOME_SLIDES[slideIndex];
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-carousel">
          <div className="onboarding-slide">
            <div className="onboarding-emoji">{slide.emoji}</div>
            <h2 className="onboarding-slide-title">{slide.title}</h2>
            <p className="onboarding-slide-subtitle">{slide.subtitle}</p>
            <p className="onboarding-slide-desc">{slide.description}</p>
          </div>

          {/* Dots */}
          <div className="onboarding-dots">
            {WELCOME_SLIDES.map((_, i) => (
              <button
                key={i}
                className={`onboarding-dot ${i === slideIndex ? 'active' : ''}`}
                onClick={() => setSlideIndex(i)}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="onboarding-nav">
            <button className="btn btn-ghost-app btn-sm" onClick={skipCarousel}>
              Skip
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              {slideIndex > 0 && (
                <button className="btn btn-outline btn-sm" onClick={prevSlide}>
                  Back
                </button>
              )}
              <button className="btn btn-primary-app btn-sm" onClick={nextSlide}>
                {slideIndex === WELCOME_SLIDES.length - 1 ? 'Start tour' : 'Next'}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="onboarding-footer">
            <AscendingK size={12} color="#C9A961" />
            <span>connekted in</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Phase 2: Tooltip tour ──
  if (phase === 'tour') {
    const step = TOUR_STEPS[tourStep];
    return (
      <>
        {/* Semi-transparent backdrop (click to skip) */}
        <div className="tour-backdrop" onClick={finishTour} />

        {/* Tooltip */}
        <div
          className="tour-tooltip"
          ref={tooltipRef}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateY(-50%)',
          }}
        >
          <div className="tour-tooltip-arrow" />
          <div className="tour-tooltip-step">
            {tourStep + 1} of {TOUR_STEPS.length}
          </div>
          <h3 className="tour-tooltip-title">{step.title}</h3>
          <p className="tour-tooltip-desc">{step.description}</p>
          <div className="tour-tooltip-nav">
            {tourStep > 0 ? (
              <button className="btn btn-ghost-app btn-sm" onClick={prevTourStep}>Back</button>
            ) : (
              <button className="btn btn-ghost-app btn-sm" onClick={finishTour}>Skip</button>
            )}
            <button className="btn btn-primary-app btn-sm" onClick={nextTourStep}>
              {tourStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return null;
}
